import { useMemo } from "react"
import { ConnectionHandler } from "relay-runtime"
import { useUser } from "@/apis/bff"
import {
  episodeOfCareProgressNotesConnectionKey,
  getEpisodeOfCareProgressNotesConnectionId,
} from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-connection"
import { isTreatmentPlanEmpty } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { isAnamnesisEmpty } from "@/modules/patients/detail/progress-note/progress-note-readonly-card-body"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { areDocumentsEmpty } from "@/modules/patients/libs/areEmptyDocuments"
import { chPatientEpisodesOfCareQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientEpisodesOfCareQuery.graphql"
import { dePatientEpisodesOfCareQuery$data } from "@/modules/patients/services/de/__generated__/dePatientEpisodesOfCareQuery.graphql"

export const useProgressNoteData = (
  patientEpisodesOfCareRef: dePatientEpisodesOfCareQuery$data | chPatientEpisodesOfCareQuery$data,
  isEditMode: boolean,
) => {
  const country = useCountryParam()
  const { userName } = useUser()
  const appointmentId = useAppointmentParam()

  const progressNoteData = useMemo(() => {
    const progressNoteAndParentDatasTmp: ProgressNoteAndParentData[] = []

    const progressNoteDeleteData: {
      id: string
      connectionId: string
    }[] = []

    const progressNoteRemoveData: {
      id: string
      connectionId: string
    }[] = []

    if (patientEpisodesOfCareRef.patientEpisodesOfCare?.edges) {
      for (const patientEpisodeOfCareEdge of patientEpisodesOfCareRef.patientEpisodesOfCare.edges) {
        const patientEpisodesOfCareEle = patientEpisodeOfCareEdge.node

        if (patientEpisodesOfCareEle.progressNotes?.edges) {
          for (const patientEpisodesOfCareProgressNoteEdge of patientEpisodesOfCareEle.progressNotes.edges) {
            const progressNoteFragmentReference = patientEpisodesOfCareProgressNoteEdge.node
            const progressNote = progressNoteFragmentReference

            const { anamnesis, pointOfCare, treatmentPlan, followUpAppointment } = progressNote
            const isEmptyProgressNote =
              isAnamnesisEmpty(anamnesis) &&
              !pointOfCare &&
              isTreatmentPlanEmpty(treatmentPlan) &&
              areDocumentsEmpty(country, progressNote) &&
              !followUpAppointment

            const hasSameCreatorAndAppointment =
              progressNote.createdBy === userName && progressNote.appointment.id === appointmentId

            // We should hide the empty progress notes when:
            // - we are in read mode
            // - we are in edit mode and they belong to another user (in this case they will even be removed)
            const showProgressNote = !isEmptyProgressNote || (hasSameCreatorAndAppointment && isEditMode)
            const shouldDeleteEmptyProgressNote = isEmptyProgressNote && !hasSameCreatorAndAppointment && isEditMode

            const episodeOfCareId = patientEpisodesOfCareEle.id
            const connectionId = getEpisodeOfCareProgressNotesConnectionId(episodeOfCareId)

            if (showProgressNote) {
              progressNoteAndParentDatasTmp.push({
                parentEpisodeOfCare: patientEpisodesOfCareEle,
                episodeOfCareProgressNoteData: progressNote,
              })
            } else if (shouldDeleteEmptyProgressNote) {
              progressNoteDeleteData.push({ id: progressNote.id, connectionId: connectionId })
            } else {
              const connectionId = ConnectionHandler.getConnectionID(
                patientEpisodesOfCareEle.id,
                episodeOfCareProgressNotesConnectionKey,
              )

              progressNoteRemoveData.push({ id: progressNote.id, connectionId: connectionId })
            }
          }
        }
      }
    }

    return {
      progressNoteAndParentData: progressNoteAndParentDatasTmp.sort((a, b) =>
        b.episodeOfCareProgressNoteData.updatedAt.localeCompare(a.episodeOfCareProgressNoteData.updatedAt),
      ),
      progressNoteDeleteData,
      progressNoteRemoveData,
    }
  }, [patientEpisodesOfCareRef.patientEpisodesOfCare?.edges, country, appointmentId, userName, isEditMode])

  return progressNoteData
}
